import { ShopigurusHTMLElement } from '@shopigurus/base';
import flowState from '@shopigurus/flow-state';

/**
 * ShpfltCartModeToggle — Segmented control showing the user's chosen
 * delivery method in the cart, with the slot returned by Zapiet Eats.
 *
 * Read-only view of Zapiet state — clicking either option opens the
 * Zapiet modal so the user changes their selection through the app's
 * native UI (Zapiet Eats does not expose a public setter).
 *
 * @example
 * <shpflt-cart-mode-toggle data-shpflt>…</shpflt-cart-mode-toggle>
 *
 * @listens {zapieteats:settings:loaded}
 * @listens {zapieteats:deliveryoption:changed}
 * @listens {liquid-ajax-cart:request-end}
 * @listens {flow:mode-changed}
 */
class ShpfltCartModeToggle extends ShopigurusHTMLElement {
  connectedCallback() {
    super.connectedCallback();

    const signal = this.abortController.signal;

    // Click handlers — open Zapiet modal so user picks slot/method
    for (const btn of this.querySelectorAll('[data-mode]')) {
      btn.addEventListener('click', (e) => this.#handleClick(e), { signal });
    }

    // Refresh whenever Zapiet's state changes — use retry because
    // `settings:loaded` often fires before `getSelectedDate()` is populated.
    const zapietRefresh = () => this.#refreshWithRetry();
    document.addEventListener('zapieteats:settings:loaded', zapietRefresh, { signal });
    document.addEventListener('zapieteats:deliveryoption:changed', zapietRefresh, { signal });
    document.addEventListener('zapieteats:restaurant:changed', zapietRefresh, { signal });
    document.addEventListener('zapieteats:modal:closed', zapietRefresh, { signal });

    // Refresh after any cart mutation. liquid-ajax-cart re-renders the
    // cart section, which on this theme also causes Zapiet Eats to
    // temporarily detach (`window.ZapietEats` goes undefined for ~1-3s
    // while it re-initializes). Retry so the pill picks up the real
    // state as soon as Zapiet is back.
    document.addEventListener(
      'liquid-ajax-cart:request-end',
      () => this.#refreshWithRetry(),
      { signal }
    );

    // Refresh when the user picks an option in the welcome popup
    document.addEventListener('flow:mode-changed', () => this.#refreshWithRetry(), { signal });

    // Initial paint with the same retry pattern (same Zapiet
    // detach/re-init race when the section morph creates this element).
    this.#refreshWithRetry();
  }

  #refreshWithRetry() {
    // Stop any retry chain already in flight before starting a new one.
    this.#retryToken = (this.#retryToken || 0) + 1;
    const myToken = this.#retryToken;

    let tries = 0;
    // ~10s budget: Zapiet's detach + re-init can run several seconds after
    // cart AJAX morphs, and getSelectedDate() often lags getDeliveryOption().
    const maxTries = 50;
    const interval = 200;

    const tick = () => {
      if (!this.isConnected) return;
      if (myToken !== this.#retryToken) return; // newer retry chain in flight
      this.#refresh();
      tries += 1;
      // Keep polling until the active tab has slot text (or there is no
      // committed mode to show). flowState can resolve the mode instantly
      // while Zapiet is still booting — stopping early caused "No disponible"
      // to stick for seconds until a later event fired.
      if (this.#isSlotResolved() || tries >= maxTries) return;
      window.setTimeout(tick, interval);
    };
    queueMicrotask(tick);
  }

  /** @type {number} */
  #retryToken = 0;

  /** @param {MouseEvent} e */
  #handleClick(e) {
    const btn = e.currentTarget;
    if (btn.dataset.disabled === 'true') return;

    const mode = btn.dataset.mode; // 'pickup' | 'shipping'

    // The cart drawer sits at a higher z-index than the Zapiet modal,
    // so opening Zapiet while the drawer is open hides Zapiet behind the
    // drawer's overlay. Close the drawer first via the theme's own close
    // button (which already cleans up body classes), then open Zapiet.
    this.#closeCartDrawer();

    flowState.setMode(mode);
    flowState.requestZapiet(mode);
  }

  #closeCartDrawer() {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    const closeBtn = drawer.querySelector('.drawer__close button, .drawer__close [type="button"]');
    if (closeBtn) {
      closeBtn.click();
      return;
    }
    // Fallback: remove the body-level open class the theme uses
    document.body.classList.remove('js-drawer-open-right');
  }

  #refresh() {
    const { pickupBtn, shippingBtn, pickupSlot, shippingSlot } = this.refs;
    if (!pickupBtn || !shippingBtn) return;

    const active = this.#resolveActiveMode();

    pickupBtn.setAttribute('aria-checked', String(active === 'pickup'));
    shippingBtn.setAttribute('aria-checked', String(active === 'shipping'));

    const slotText = this.#resolveSlotText(active);
    const waitingForSlot = Boolean(active) && !slotText && !this.#isZapietReady();

    // Show the slot on whichever option is active; show "No disponible" on the inactive one.
    if (active === 'pickup') {
      this.#setSlot(pickupSlot, slotText, waitingForSlot);
      this.#setSlot(shippingSlot, shippingSlot?.dataset.emptyText || '');
    } else if (active === 'shipping') {
      this.#setSlot(shippingSlot, slotText, waitingForSlot);
      this.#setSlot(pickupSlot, pickupSlot?.dataset.emptyText || '');
    } else {
      this.#setSlot(pickupSlot, pickupSlot?.dataset.emptyText || '');
      this.#setSlot(shippingSlot, shippingSlot?.dataset.emptyText || '');
    }
  }

  /**
   * True when there is nothing left to wait for, or the active tab already
   * has displayable slot text.
   * @returns {boolean}
   */
  #isSlotResolved() {
    const active = this.#resolveActiveMode();
    if (!active) return true;
    return Boolean(this.#resolveSlotText(active));
  }

  /** @returns {boolean} */
  #isZapietReady() {
    try {
      const zapiet = window.ZapietEats;
      return Boolean(
        zapiet &&
          (typeof zapiet.getSelectedDate === 'function' ||
            typeof zapiet.getAttributes === 'function')
      );
    } catch {
      return false;
    }
  }

  /** @returns {'pickup'|'shipping'|null} */
  #resolveActiveMode() {
    // Priority: Zapiet's own state, then flow state, then null.
    try {
      const zapiet = window.ZapietEats;
      if (zapiet && typeof zapiet.getDeliveryOption === 'function') {
        const opt = zapiet.getDeliveryOption();
        if (opt === 'pickup' || opt === 'delivery') {
          return opt === 'delivery' ? 'shipping' : 'pickup';
        }
      }
    } catch {
      /* Zapiet not ready — fall through */
    }
    const mode = flowState.getMode();
    if (mode === 'pickup' || mode === 'shipping') return mode;
    return null;
  }

  /**
   * @param {'pickup'|'shipping'|null} mode
   * @returns {string}
   */
  #resolveSlotText(mode) {
    const fromZapiet = this.#resolveSlotTextFromZapiet(mode);
    if (fromZapiet) return fromZapiet;

    const fromStorage = this.#resolveSlotTextFromStorage(mode);
    if (fromStorage) return fromStorage;

    const fromTopBar = this.#resolveSlotTextFromTopBar();
    return fromTopBar ? this.#applyModeSlotCopy(fromTopBar, mode) : '';
  }

  /**
   * Zapiet stores date and time separately. `getSelectedDate()` alone often
   * lacks the slot time (or carries the wrong clock value) — pair it with
   * `getSelectedTime()`, cart attributes, or the ZapietId string.
   *
   * @param {'pickup'|'shipping'|null} mode
   * @returns {string}
   */
  #resolveSlotTextFromZapiet(mode) {
    try {
      const zapiet = window.ZapietEats;
      if (!zapiet) return '';

      const fromAttrs = this.#resolveSlotFromZapietAttributes(mode);
      if (fromAttrs) return fromAttrs;

      const fromId = this.#resolveSlotFromZapietId(mode);
      if (fromId) return fromId;

      const datePart =
        typeof zapiet.getSelectedDate === 'function'
          ? this.#extractZapietDate(zapiet.getSelectedDate())
          : '';
      const timePart =
        typeof zapiet.getSelectedTime === 'function'
          ? this.#extractZapietTime(zapiet.getSelectedTime())
          : '';

      if (datePart || timePart) {
        return this.#combineDateAndTime(datePart, timePart, mode);
      }
    } catch {
      /* Zapiet not ready */
    }
    return '';
  }

  /**
   * @param {'pickup'|'shipping'|null} mode
   * @returns {string}
   */
  #resolveSlotFromZapietAttributes(mode) {
    const zapiet = window.ZapietEats;
    if (!zapiet || typeof zapiet.getAttributes !== 'function') return '';

    const map = this.#attributesToMap(zapiet.getAttributes());
    const isPickup = mode === 'pickup';
    const dateVal = isPickup ? map['Pickup-Date'] : map['Delivery-Date'];
    const timeVal = isPickup ? map['Pickup-Time'] : map['Delivery-Time'];
    return this.#combineDateAndTime(dateVal, timeVal, mode);
  }

  /**
   * @param {'pickup'|'shipping'|null} mode
   * @returns {string}
   */
  #resolveSlotFromZapietId(mode) {
    const zapiet = window.ZapietEats;
    if (!zapiet || typeof zapiet.getZapietId !== 'function') return '';

    const raw = zapiet.getZapietId();
    if (!raw || typeof raw !== 'string') return '';

    const params = {};
    for (const segment of raw.split('&')) {
      const eq = segment.indexOf('=');
      if (eq === -1) continue;
      params[segment.slice(0, eq)] = decodeURIComponent(segment.slice(eq + 1));
    }

    return this.#combineDateAndTime(params.D, params.T, mode);
  }

  /** @param {unknown} attrs */
  #attributesToMap(attrs) {
    /** @type {Record<string, string>} */
    const map = {};
    if (Array.isArray(attrs)) {
      for (const entry of attrs) {
        if (entry?.key) map[entry.key] = String(entry.value ?? '');
      }
      return map;
    }
    if (attrs && typeof attrs === 'object') {
      for (const [key, value] of Object.entries(attrs)) {
        map[key] = String(value ?? '');
      }
    }
    return map;
  }

  /** @param {unknown} date */
  #extractZapietDate(date) {
    if (!date) return '';
    if (typeof date === 'string') return date;
    if (date instanceof Date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
    if (typeof date === 'object') {
      const record = /** @type {Record<string, unknown>} */ (date);
      const candidate =
        record.date ||
        record.value ||
        record.iso ||
        record.formatted_date ||
        record.formattedDate;
      return candidate ? String(candidate) : '';
    }
    return '';
  }

  /** @param {unknown} time */
  #extractZapietTime(time) {
    if (!time) return '';
    if (typeof time === 'string') return time;
    if (typeof time === 'object') {
      const record = /** @type {Record<string, unknown>} */ (time);
      const candidate =
        record.formatted ||
        record.label ||
        record.start_time ||
        record.startTime ||
        record.value ||
        record.time;
      return candidate ? String(candidate) : '';
    }
    return '';
  }

  /**
   * @param {unknown} datePart
   * @param {unknown} timePart
   * @param {'pickup'|'shipping'|null} mode
   * @returns {string}
   */
  #combineDateAndTime(datePart, timePart, mode) {
    const dateStr = this.#normalizeDateInput(datePart);
    const timeStr = this.#normalizeTimeInput(timePart);

    if (!dateStr) return '';

    const [year, month, day] = dateStr.split('-').map(Number);
    let hours = 0;
    let minutes = 0;

    if (timeStr) {
      [hours, minutes] = timeStr.split(':').map(Number);
    } else if (mode === 'pickup') {
      // Date without a slot time — try another source instead of guessing.
      return '';
    }

    const local = new Date(year, month - 1, day, hours, minutes);
    if (Number.isNaN(local.getTime())) return '';
    return this.#formatLocalDate(local, mode);
  }

  /** @param {unknown} input @returns {string} YYYY-MM-DD */
  #normalizeDateInput(input) {
    if (!input) return '';
    const str = String(input).trim();
    const iso = str.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

    const parsed = new Date(str);
    if (Number.isNaN(parsed.getTime())) return '';
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
  }

  /** @param {unknown} input @returns {string} HH:mm 24h */
  #normalizeTimeInput(input) {
    if (!input) return '';
    const str = String(input).trim();

    const h24 = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (h24) {
      return `${String(Number(h24[1])).padStart(2, '0')}:${h24[2]}`;
    }

    const h12 = str.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (h12) {
      let hours = Number(h12[1]);
      const minutes = h12[2];
      if (/pm/i.test(h12[3]) && hours < 12) hours += 12;
      if (/am/i.test(h12[3]) && hours === 12) hours = 0;
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }

    return '';
  }

  /**
   * @param {'pickup'|'shipping'|null} mode
   * @returns {string}
   */
  #resolveSlotTextFromStorage(mode) {
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (!key) continue;

        if (key.endsWith('.searchRequest')) {
          const obj = JSON.parse(sessionStorage.getItem(key) || 'null');
          const dateVal =
            obj?.date ||
            obj?.pickupDate ||
            obj?.deliveryDate ||
            obj?.fulfilmentDate ||
            obj?.selectedDate;
          const timeVal =
            obj?.start_time ||
            obj?.startTime ||
            obj?.time ||
            obj?.selectedTime ||
            obj?.pickupTime ||
            obj?.deliveryTime;
          const formatted = this.#combineDateAndTime(dateVal, timeVal, mode);
          if (formatted) return formatted;
        }

        if (
          key.endsWith('.selectedDate') ||
          key.endsWith('.deliveryDate') ||
          key.endsWith('.pickupDate') ||
          key.endsWith('.fulfilmentDate')
        ) {
          const raw = sessionStorage.getItem(key);
          if (!raw || raw === 'null' || raw === '""') continue;
          let value = raw;
          try {
            const parsed = JSON.parse(raw);
            if (parsed) value = parsed;
          } catch {
            /* raw string date */
          }
          const formatted = this.#combineDateAndTime(value, '', mode);
          if (formatted) return formatted;
        }

        if (key.endsWith('.selectedTime') || key.endsWith('.pickupTime') || key.endsWith('.deliveryTime')) {
          const timeVal = sessionStorage.getItem(key);
          if (!timeVal || timeVal === 'null') continue;
          // Pair with the first date key we can find in the same searchRequest pass.
          for (let j = 0; j < sessionStorage.length; j++) {
            const dateKey = sessionStorage.key(j);
            if (!dateKey?.endsWith('.searchRequest')) continue;
            const obj = JSON.parse(sessionStorage.getItem(dateKey) || 'null');
            const dateVal =
              obj?.date ||
              obj?.pickupDate ||
              obj?.deliveryDate ||
              obj?.selectedDate;
            const formatted = this.#combineDateAndTime(dateVal, timeVal, mode);
            if (formatted) return formatted;
          }
        }
      }
    } catch {
      /* sessionStorage unavailable */
    }
    return '';
  }

  /**
   * Last-resort: mirror what Zapiet already shows in its top bar while the
   * JS API is still booting.
   * @returns {string}
   */
  #resolveSlotTextFromTopBar() {
    const el =
      document.querySelector('.ZapietEats-TopBar-SecondaryText') ||
      document.querySelector('[class*="ZapietEats-TopBar"] [class*="SecondaryText"]');
    const text = el?.textContent?.trim() || '';
    return text && !/^(recogida|env[ií]o|pickup|delivery)$/i.test(text) ? text : '';
  }

  /**
   * @param {Date} date — local date/time (not UTC-parsed ISO)
   * @param {'pickup'|'shipping'|null} [mode]
   * @returns {string}
   */
  #formatLocalDate(date, mode = null) {
    if (Number.isNaN(date.getTime())) return '';
    const locale = document.documentElement.lang || 'es-ES';
    const day = date.toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
    });
    const formattedDay = day.charAt(0).toUpperCase() + day.slice(1);

    if (mode === 'shipping') {
      return `${formattedDay}, ${this.#shippingDaySuffix()}`;
    }

    const time = date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${formattedDay}, ${time}`;
  }

  /** @returns {string} */
  #shippingDaySuffix() {
    const { shippingSlot } = this.refs;
    return shippingSlot?.dataset.dayRangeText || 'a lo largo del día';
  }

  /**
   * Align pre-formatted Zapiet top-bar copy with cart toggle rules.
   * @param {string} text
   * @param {'pickup'|'shipping'|null} mode
   * @returns {string}
   */
  #applyModeSlotCopy(text, mode) {
    if (mode !== 'shipping') return text;
    if (/a lo largo del d[ií]a/i.test(text)) return text;

    const suffix = this.#shippingDaySuffix();
    const withoutClock = text.replace(
      /,?\s*\d{1,2}:\d{2}(\s*(?:AM|PM|a\.?\s*m\.?|p\.?\s*m\.?)?)?$/i,
      ''
    );
    if (withoutClock !== text) {
      return `${withoutClock.replace(/,\s*$/, '')}, ${suffix}`;
    }

    return text.replace(/a\s+las\s+\d{1,2}:\d{2}(\s*(?:AM|PM))?/i, suffix);
  }

  /**
   * @param {HTMLElement|undefined} el
   * @param {string} text
   * @param {boolean} [loading=false]
   */
  #setSlot(el, text, loading = false) {
    if (!el) return;
    if (text) {
      el.textContent = text;
      el.removeAttribute('data-loading');
      return;
    }
    if (loading) {
      el.textContent = el.dataset.loadingText || 'Cargando…';
      el.dataset.loading = 'true';
      return;
    }
    el.textContent = el.dataset.emptyText || '';
    el.removeAttribute('data-loading');
  }
}

customElements.define('shpflt-cart-mode-toggle', ShpfltCartModeToggle);
